<!-- eslint-disable import/extensions -->
<script lang="ts">
import { defineComponent, ref, nextTick, onMounted, computed } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { startCase, noop } from 'lodash'
import Cookies from 'js-cookie'
import { toastService } from '@siteminder/sui-core'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import {
  COOKIES_TOKEN_KEY,
  LOCAL_STORAGE_EMAIL_KEY,
  LOCAL_STORAGE_LAST_LOGIN_EMAIL_KEY,
  LOCAL_STORAGE_REDIRECT_URI_KEY,
  LOCAL_STORAGE_IID_KEY,
  SESSION_FORCE_MFA_TOTP_ONLY_KEY,
} from '@/constants'
import { useRoute, router } from '../../router'
import { useI18n } from '../../setup/setup-i18n'
import { redirectToUniversalLogin } from '../../services/auth/universal'
import { nrLoginEmail } from '../../services/newrelic'
import { NR_STEPS } from '../../services/newrelic/constants'
import { clearSessionStep, saveSessionStart, saveIncompleteStep } from '../../services/auth/authx-session'
import { AuthPurpose, Partner, PartnerDisplayName } from '../../types'
import config from '../../config'

const { useState: useUserStoreState, useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState, useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

enum Stage {
  Email = 'Email',
  Password = 'Password',
}

export default defineComponent({

  name: 'LoginPage',

  components: { DefaultLayout },

  setup() {

    saveSessionStart()
    const { user } = useUserStoreState(['user'])
    const { loginViaSso, authorizeRedirectUri, authenticateAgainstAuth0 } = useUserStoreActions([
      'loginViaSso',
      'authorizeRedirectUri',
      'authenticateAgainstAuth0',
    ])
    const { t, locale } = useI18n()
    const { tenant } = useLoginDomainStoreState(['tenant'])
    const { loginHelpUrl, loginHelpUrlLocales, logo, themeCode, themeName } = useLoginDomainStoreGetters(['loginHelpUrl', 'loginHelpUrlLocales', 'logo', 'themeCode', 'themeName'])

    const useAuthxTheme = config.USE_AUTHX_THEME_LOGO_URL === 'true'

    let displayLogo: string
    let displayThemeName: string

    if (useAuthxTheme) {
      displayLogo = logo.value
      displayThemeName = themeName.value
    } else {
      const isLittleHotelier = themeCode.value === Partner.LittleHotelier
      displayLogo = isLittleHotelier ? config.LITTLE_HOTELIER_THEME_LOGO_URL : config.SITEMINDER_THEME_LOGO_URL
      displayThemeName = isLittleHotelier ? PartnerDisplayName.LittleHotelier : PartnerDisplayName.SiteMinder
    }

    const localisedHelpUrl = computed(() => {
      if (!loginHelpUrlLocales.value) {
        return ''
      }
      return loginHelpUrlLocales.value[locale] || loginHelpUrlLocales.value.en
    })

    const loading = ref(false)
    const visible = ref(false)
    const rememberEmail = ref(false)
    const errorMessage = ref(null)
    const showPassword = ref(false)
    const form = ref(null)
    const email = ref(null)
    const password = ref(null)
    const showLoginPasskeyButton = ref(false)

    const currentRoute = useRoute()

    const getRedirectUri = (): string => {
      return currentRoute.value?.query.redirectUri as string
    }

    const getPartnerCode = (): string => {
      return currentRoute.value?.query.partnerCode as string
    }

    const getIID = (): string => {
      const iid = localStorage.getItem(LOCAL_STORAGE_IID_KEY)
      return currentRoute.value?.query.iid as string || iid
    }

    const getStage = computed(() => {
      return (startCase(currentRoute.value?.params.stage) as Stage) || Stage.Email
    })

    const storeIID = () => {
      const iid = getIID()
      if (iid) {
        localStorage.setItem(LOCAL_STORAGE_IID_KEY, iid)
      } else {
        localStorage.removeItem(LOCAL_STORAGE_IID_KEY)
      }
    }

    const setStage = (stage: Stage) => {
      router.push({
        params: {
          stage: stage.toLowerCase(),
        },
      })
    }

    const navigateFromPasswordToEmail = () => {
      clearError()

      // remove email step in session
      clearSessionStep()

      router.push({
        params: {
          stage: null,
        },
      })
    }

    const getPageTitle = () => {
      if (currentRoute.value?.query.activateSuccess) {
        return t('LoginPage.activateSuccessfulPageTitle') as string
      }
      return t('LoginPage.pageTitle') as string
    }

    const rejectSubmission = () => {
      errorMessage.value = t('LoginPage.error.invalidEmailOrPassword') as string
    }

    const selectEmailField = () => {
      if (email.value && email.value.input) {
        (email as any).value.input.focus()
      }
    }

    const selectPasswordField = () => {
      if (password.value && password.value.input) {
        (password as any).value.input.focus()
      }
    }

    const applyCachedEmail = () => {
      user.value.email = currentRoute.value?.query.email as string
        || localStorage.getItem(LOCAL_STORAGE_EMAIL_KEY)
        || user.value?.email
    }

    const storeRedirectURI = () => {
      const redirectUri = getRedirectUri()

      if (redirectUri) {
        localStorage.setItem(LOCAL_STORAGE_REDIRECT_URI_KEY, redirectUri)
      } else {
        localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI_KEY)
      }
    }

    const setRememberEmail = () => {
      if (localStorage.getItem(LOCAL_STORAGE_EMAIL_KEY)) {
        rememberEmail.value = true
      }
    }

    const validatePasswordStage = () => {
      if (!user.value?.email && getStage.value === Stage.Password) {
        saveIncompleteStep(NR_STEPS.LOGIN_PASSWORD)
        setStage(Stage.Email)
      }
    }

    const clearError = () => {
      errorMessage.value = null
    }

    const toggleShowHidePassword = () => {
      showPassword.value = !showPassword.value
    }

    const handleSubmit = async () => {

      clearError()

      return (form as any).value?.validate().then(async (valid) => {

        if (!valid) {
          return
        }

        loading.value = true

        try {

          if (getStage.value === Stage.Email) {
            // Show the password field
            nrLoginEmail()
            saveIncompleteStep(NR_STEPS.LOGIN_PASSWORD)
            setStage(Stage.Password)
            nextTick(() => {
              selectPasswordField()
            })

          } else {
            // Store the user's email, which will be used on subsequent logins
            if (rememberEmail.value) {
              localStorage.setItem(LOCAL_STORAGE_EMAIL_KEY, user.value?.email)
            } else {
              localStorage.removeItem(LOCAL_STORAGE_EMAIL_KEY)
            }

            localStorage.setItem(LOCAL_STORAGE_LAST_LOGIN_EMAIL_KEY, user.value?.email || '')

            await authenticateAgainstAuth0({
              user: user.value,
              tenant: tenant.value,
            })
          }

        } catch (error) {
          // Account disabled at Auth0
          if (['too_many_attempts', 'password_leaked'].indexOf(error.code) !== -1) {
            router.push({ name: 'AccountDisabledErrorPage' })
            return
          }
          // Network error
          if ((error.request && !error.response) || error.code === 'request_error') {
            errorMessage.value = t('LoginPage.error.networkError') as string
            return
          }
          // Invalid credentials
          rejectSubmission()
        } finally {
          loading.value = false
        }

      }).catch(noop)

    }

    const handleLoginPasskey = async () => {

      await redirectToUniversalLogin({
        tenant: tenant.value,
        redirectUri: `${window.location.protocol}//${window.location.host}/authenticate`,
        // a valid session should silently SSO into the passkey verification, so only
        // force a full re-authentication when this page was opened with prompt=login
        // (e.g. the passkey enrolment gate bouncing a stale session) — otherwise Auth0
        // re-issues the stale session and the caller loops straight back here
        prompt: currentRoute.value?.query.prompt === 'login' ? 'login' : undefined,
        temporaryMfaSetup: false,
        logoUri: logo.value,
        purpose: AuthPurpose.passkeyLogin,
      })

    }

    // Shows the login form (and sets up the passkey button where the device
    // supports it). Called when the silent SSO fails, or when prompt=login is
    // set to force an interactive login — a stale-but-valid session would
    // otherwise be re-issued by checkSession (no re-authentication) and loop
    // the caller back to where they started (e.g. the passkey enrolment gate).
    const showLoginForm = () => {

      saveSessionStart()
      applyCachedEmail()
      setRememberEmail()
      visible.value = true

      const publicKeyCredential = window.PublicKeyCredential as any

      if (publicKeyCredential && publicKeyCredential.getClientCapabilities) {

        publicKeyCredential.getClientCapabilities().then((value) => {

          const iid = getIID()
          const { enablePasskey } = tenant.value

          const { passkeyPlatformAuthenticator } = value

          if (!iid && enablePasskey && passkeyPlatformAuthenticator) {
            showLoginPasskeyButton.value = true
          }

          if (!iid && passkeyPlatformAuthenticator && config.PASSKEYS_WHITELISTED_USERS) {
            const whitelistedUsers = config.PASSKEYS_WHITELISTED_USERS.split(',')
            const currentUserEmail = user.value.email

            if (whitelistedUsers.includes(currentUserEmail)) {
              showLoginPasskeyButton.value = true
            }

          }

        }).catch((error) => {
          if (error instanceof TypeError) {
            console.log(
              'PublicKeyCredential.getClientCapabilities() is not supported on this browser.',
            )
          }
        })

      }

    }

    onMounted(async () => {

      // Store the origin-provided redirect URI
      storeRedirectURI()

      // Store the mobile install-id
      storeIID()

      const hasRedirectUri = !!currentRoute.value?.query.redirectUri
      const temporaryMfaSetup = !!currentRoute.value?.query.temporaryMfaSetup
      const purpose = currentRoute.value?.query.purpose
      const prompt = currentRoute.value?.query.prompt
      const forceMfa = currentRoute.value?.query.force_mfa === 'true'

      // force_mfa=true (MFA enrolment, SM Mobile or TOTP): drop the existing authx session so the user must
      // log in again. The interactive login lands on the MFA setup screen when the user has no MFA.
      if (forceMfa) {
        Cookies.remove(COOKIES_TOKEN_KEY)
      }

      // force_mfa=true with prompt=login (no iid) is the browser MFA enrolment: remember it across the
      // Auth0 redirect so MfaSetupPage only offers TOTP registration (no SM Mobile)
      if (forceMfa && prompt === 'login') {
        sessionStorage.setItem(SESSION_FORCE_MFA_TOTP_ONLY_KEY, 'true')
      } else {
        sessionStorage.removeItem(SESSION_FORCE_MFA_TOTP_ONLY_KEY)
      }

      // prompt=login / force_mfa: skip the silent SSO and show the login form directly so
      // the user re-authenticates interactively (password or passkey).
      if (prompt === 'login' || forceMfa) {
        showLoginForm()
      } else {
        // Check if the user's session is in tact
        // If so, redirect to /authenticate
        // If not, proceed to the login flow
        await loginViaSso({ tenant: tenant.value, applyMfa: !hasRedirectUri, temporaryMfaSetup, purpose })
          .catch(showLoginForm)
      }

      validatePasswordStage()

      if (getStage.value === Stage.Email) {
        selectEmailField()
      }

      if (currentRoute.value?.query.loggedOut) {
        toastService({
          type: 'success',
          message: t('LoginPage.loggedOutSuccessful') as string,
          showClose: true,
        })
      }

      if (currentRoute.value?.query.resetPassword) {
        toastService({
          type: 'success',
          message: t('LoginPage.resetPasswordSuccessful') as string,
          showClose: true,
        })

      }

    })

    return {
      t,
      visible,
      getPageTitle,
      user,
      tenant,
      handleSubmit,
      getStage,
      setStage,
      clearError,
      loading,
      rememberEmail,
      showPassword,
      toggleShowHidePassword,
      loginHelpUrl,
      errorMessage,
      getPartnerCode,
      authorizeRedirectUri,
      selectPasswordField,
      form,
      password,
      email,
      rejectSubmission,
      navigateFromPasswordToEmail,
      localisedHelpUrl,
      handleLoginPasskey,
      showLoginPasskeyButton,
      displayLogo,
      displayThemeName,
    }

  },

})
</script>

<template>
  <default-layout
    v-show="visible"
    class="login"
    :title="getPageTitle()"
    :sub-title="t('LoginPage.subTitle')"
    :logo-src="displayLogo"
    :logo-alt="displayThemeName"
  >
    <sm-form
      id="login-form"
      ref="form"
      autocomplete="on"
      @submit.native.prevent="handleSubmit"
    >
      <fieldset>
        <div
          v-if="getStage === 'Password'"
          class="user-entered-email-container"
        >
          <sm-button
            aria-label="Close"
            shape="square"
            type="text"
            @click="navigateFromPasswordToEmail"
          >
            <sm-icon name="arrow-go-back" />
          </sm-button>
          <div class="user-entered-email">
            <h6 :title="user.email">
              {{ user.email }}
            </h6>
          </div>
        </div>
        <div
          v-if="getStage === 'Email'"
          :disabled="loading"
        >
          <sm-input
            id="login-email"
            key="email"
            v-model="user.email"
            type="email"
            name="username"
            rules="required|email"
            :label="t('LoginPage.emailLabel')"
            :placeholder="t('LoginPage.emailPlaceholder')"
            autofocus
            auto-complete="username"
          />
          <div class="login-remember-container">
            <sm-checkbox
              id="login-remember"
              v-model="rememberEmail"
              :disabled="loading"
              name="genres"
              :label="t('LoginPage.rememberEmailLabel')"
              selected-value="classical"
            />
          </div>
        </div>
        <div
          v-else
          data-heap-redact-attributes="value"
          data-heap-redact-text="true"
          class="password-stage"
          :disabled="loading"
        >
          <!-- for autocomplete to work, it needs username and password input to be rendered together. -->
          <input
            name="username"
            class="sr-only"
            autocomplete="username"
            :value="user.email"
          >
          <sm-input
            key="password"
            v-model="user.password"
            data-heap-redact-text="true"
            class="login-password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            :label="t('LoginPage.passwordLabel')"
            rules="required"
            auto-complete="current-password"
            :placeholder="t('LoginPage.passwordPlaceholder')"
            autofocus
          >
            <template #suffix>
              <sm-button
                type="tertiary"
                @click="toggleShowHidePassword"
              >
                <sm-icon
                  :name="showPassword ? 'action-hide' : 'action-show'"
                  :class="['eye-icon', { 'hide-password': showPassword }]"
                />
              </sm-button>
            </template>
          </sm-input>
          <div class="forgot-password">
            <router-link :to="{ name: 'ResetPasswordPage' }">
              {{ t('LoginPage.resetPasswordLink') }}
            </router-link>
          </div>
        </div>
        <div>
          <sm-button
            id="login-submit"
            native-type="submit"
            type="primary"
            :loading="loading"
          >
            <template v-if="getStage === 'Email'">
              {{ t('LoginPage.nextButton') }}
            </template>
            <template v-else>
              {{ t('LoginPage.loginButton') }}
            </template>
          </sm-button>
          <div
            v-if="getStage === 'Email' && showLoginPasskeyButton"
            sm-data-test="login-passkey-button"
          >
            <div class="divider-outer">
              <div class="divider-head">
                <div class="passkey-divider" />
                <div class="passkey-divider-inner">
                  <span class="passkey-divider-text">
                    {{ t('LoginPage.passkeyDivider') }}
                  </span>
                </div>
                <div class="passkey-divider" />
              </div>
            </div>

            <sm-button
              id="login-passkey"
              type="secondary"
              :loading="loading"
              @click="handleLoginPasskey"
            >
              <template>
                {{ t('LoginPage.loginPasskeyButton') }}
              </template>
            </sm-button>
          </div>
        </div>
        <sm-help-card
          v-if="errorMessage"
          type="warning"
        >
          <template #header>
            {{ errorMessage }}
          </template>
        </sm-help-card>
        <div
          v-if="(localisedHelpUrl || loginHelpUrl) && getStage === 'Password'"
          class="login-help"
        >
          <a
            class="el-button el-button--text"
            :href="localisedHelpUrl || loginHelpUrl"
            target="_blank"
          >
            <sm-icon name="utility-information" />
            {{ t('LoginPage.troubleLoggingInLink') }}
          </a>
        </div>
      </fieldset>
    </sm-form>
  </default-layout>
</template>

<style lang="scss" scoped>
// these are style overrides for the sui components to make them look similar to the previous sputnik components.
// TODO: remove these overrides once we implement new screens.
#login-submit {
  @apply w-full mb-2.5;
}

#login-passkey {
  @apply w-full mb-2.5;
}

:deep(.sm-button__content) {
  @apply justify-center;
}

.divider-outer {
  margin-inline: auto;
  margin-top: 16px;
  margin-bottom: 26px;
}

.divider-head {
  display: flex;
  align-items: center;
}

.passkey-divider {
  width: 100%;
  border: 1px solid #e6ebf2;
  border-top-style: var(--tw-border-style);
  border-top-width: 1px;
}
.passkey-divider-text {
  font-size: .875rem;
  line-height: var(--tw-leading, calc(1.25 / .875));
  padding-inline: .5rem;
}

.passkey-divider-inner {
  display: flex;
  justify-content: center;
  position: relative;
}

.login-remember-container {
  @apply flex justify-end;
  :deep(.sm-field-error) {
    @apply hidden;
  }
}

:deep(.sm-form) {
  @apply flex justify-center flex-col;
  fieldset {
    @apply self-center sm:w-[364px] max-w-[364px] h-[300px];
    min-width: inherit;
  }
  @media only screen and (max-width: 639px) {
    fieldset {
      width: -webkit-fill-available;
      width: -moz-available;
      min-width: 280px;
    }
  }
  .sm-help-card {
    @apply my-2.5;
  }
  .sm-field-error {
    @apply py-0;
  }
}

.forgot-password {
  @apply flex justify-end mb-2;
}

.user-entered-email-container {
  @apply flex items-center mb-4;
}
.password-stage {
  ::v-deep .sm-field-error{
    @apply py-0;
  }
}

.user-entered-email {
  @apply truncate;
  h6 {
    margin-bottom: 6px !important;
    @apply truncate max-w-[274px] sm:max-w-[300px];
    width: inherit;
  }
}

 :deep(.sm-button--type-tertiary .sm-button__content) {
  border-color: #B0D2F8;
  background: #E6F0FF;
}

.login-password {
  :deep(.sm-button) {
    width: 38px;
  }
  :deep(.sm-input__field) {
    @apply border-r-0;
  }
  :deep(.sm-button__content) {
    padding: 9px 0px;
  }
  :deep(.sm-button__inner-content) {
    @apply flex items-end;
  }
}

.login-help {
  @apply flex justify-center py-2.5;
}

.eye-icon {
  @apply cursor-pointer;
}
.hide-password {
  color: #9A9A9A;
}
</style>
